import React from 'react';
import { DashboardMenuIcon } from 'shared/ui/icons';
import { menuListRole } from 'shared/constants/menu-catalog';
import { MENU, MENU_REGISTRY } from 'shared/lib/permissions';

// Quality Checker is a BE menu (slug `company-insurance-claim-qc`, id 59). It
// arrives with the BE label "Insurance Claim Qc" and an empty
// `frontend_endpoint`; buildMenuItems normalizes it to the "Quality Checker"
// label and the `/quality-checker` route so it renders once, from the BE menu
// (no FE-static duplicate).
const QC_MENU_SLUG = 'company-insurance-claim-qc';
const QC_MENU_LABEL = 'Quality Checker';
const QC_MENU_PATH = '/quality-checker';

// QC Dashboard Analytics (SA-2118) is a BE menu of its own (slug
// `company-insurance-claim-qc-analytic`, route `/qc-analytics-dashboard`) and
// renders at its BE position. The BE grants it with the Quality Checker role,
// so the item is additionally hidden while the company's QC_CLAIM feature is
// off. Matched by slug, with the endpoint as fallback — never by the
// auto-increment DB id.
const QC_ANALYTICS_MENU = MENU_REGISTRY[MENU.QC_ANALYTICS_DASHBOARD];

const isQcAnalyticsMenu = (item) =>
  item?.slug === QC_ANALYTICS_MENU.slug ||
  item?.frontend_endpoint === QC_ANALYTICS_MENU.endpoint;

// Fallback until the BE seeds the QC Analytic menu into every role: a user
// with the Quality Checker menu already passes the BE guard (QC grants QC
// Analytic at runtime), so the item is added from the FE — right after
// Dashboard Analitik, or after Quality Checker when Analytics is absent. Same
// rule as `accessFallback` in the menu registry. The BE item wins when sent.
const ANALYTICS_ENDPOINT = MENU_REGISTRY[MENU.ANALYTICS].endpoint;
const QC_ANALYTICS_FALLBACK_MENU = Object.freeze({
  id: 'fe-fallback-qc-analytics-dashboard',
  slug: QC_ANALYTICS_MENU.slug,
  name: 'Insurance Claim QC Analytic',
  frontend_endpoint: QC_ANALYTICS_MENU.endpoint,
});

const withQcAnalyticsFallback = (menus) => {
  if (menus.some(isQcAnalyticsMenu)) return menus;
  const qcIndex = menus.findIndex((item) => item?.slug === QC_MENU_SLUG);
  if (qcIndex < 0) return menus;
  const analyticsIndex = menus.findIndex(
    (item) => item?.frontend_endpoint === ANALYTICS_ENDPOINT
  );
  const insertAt = (analyticsIndex >= 0 ? analyticsIndex : qcIndex) + 1;
  return [
    ...menus.slice(0, insertAt),
    QC_ANALYTICS_FALLBACK_MENU,
    ...menus.slice(insertAt),
  ];
};

/**
 * BE menus (`useMenus`) → sidebar items: `label`, `path`, `icon` and
 * `children` (sub menus with a route). Normalizes the Quality Checker menu.
 * @param {Array<object>|null|undefined} dataMenu
 * @returns {Array<object>} new array; `dataMenu` is never mutated
 */
export const mapMenuItems = (dataMenu) =>
  (dataMenu ?? [])
    .map((item) => {
      const isQcMenu = item?.slug === QC_MENU_SLUG;
      return {
        ...item,
        label: isQcMenu ? QC_MENU_LABEL : item?.name,
        path: isQcMenu ? QC_MENU_PATH : item?.frontend_endpoint,
        // Preserve undefined when sub_menus is missing — the render branch
        // checks `item.children` (truthy) to pick collapsible vs flat layout.
        // An empty array would still be truthy and force collapsible mode.
        children: item?.sub_menus
          ?.map((sub_menu) => {
            const match = menuListRole.find(
              (menu) => menu.menu_id === sub_menu.id
            );
            return {
              ...sub_menu,
              path: sub_menu?.frontend_endpoint,
              icon: match?.icon ?? <DashboardMenuIcon />,
            };
          })
          .filter((sub_menu) => sub_menu.frontend_endpoint !== ''),
        icon: menuListRole.find((menu) => menu.menu_id === item.id)?.icon ?? (
          <DashboardMenuIcon />
        ),
      };
    })
    .filter(Boolean);

/**
 * Sidebar items from the BE menus. The QC Dashboard Analytics menu is shown
 * only while the company's QC_CLAIM feature is active (`showQcAnalytics`): at
 * its BE position when the BE sends it, otherwise as an FE fallback for users
 * with the Quality Checker menu.
 * @param {Array<object>|null|undefined} dataMenu
 * @param {{ showQcAnalytics?: boolean }} [options]
 * @returns {Array<object>} new array; `dataMenu` is never mutated
 */
export const buildMenuItems = (dataMenu, { showQcAnalytics = false } = {}) => {
  if (showQcAnalytics) {
    return mapMenuItems(withQcAnalyticsFallback(dataMenu ?? []));
  }
  return mapMenuItems(dataMenu).filter((item) => !isQcAnalyticsMenu(item));
};
